<template>
  <section class="visualization">
    <!-- <h1 v-if="!isSafari" class="visualization__current">
      {{ visualization.chartSelection.name }}
      {{ displayYear("begin") }}&ndash;{{ displayYear("end") }}
    </h1> -->

    <div
      class="visualization__inner"
      :class="{ 'visualization__inner--no-safari': isSafari }"
    >
      <div v-if="isSafari" class="visualization__no-safari">
        We're sorry, but your version of Safari does not currently support data
        visualization. Please use
        <a href="https://www.google.com/chrome/" target="_blank">Chrome</a>,
        <a href="https://getfirefox.com" target="_blank">Firefox</a>, or
        <a
          href="https://www.microsoft.com/en-us/windows/microsoft-edge"
          target="_blank"
          >Edge</a
        >.
      </div>

      <div v-else>
        <div
          class="visualization__canvas"
          :class="{ 'visualization__canvas--hide': !showChart }"
        >
          <custom-legend @hide-others="hideOtherDatasets" />
          <div v-if="isLineChart">
            <!-- <strong>Line Chart</strong> -->
            <LineChart :chart-data="chartData" :options="options" />
          </div>
          <div v-if="!isLineChart">
            <!-- <strong>Bar Chart</strong> -->
            <BarChart :chart-data="chartData" :options="options" />
          </div>
        </div>

        <div class="controls" style="margin: 1em; min-height: 1em">
          <form action="" class="at-form">
            <section class="visualization-controls__row">
              <CountySelect @child-changed="onChange" />
              <!-- <data-select @child-changed="onChange" /> -->
              <SeasonSelect @child-changed="onChange" />
            </section>
            <section class="visualization-controls__row">
              <YearSelect @child-changed="onChange" />
            </section>
            <section class="visualization-controls__row">
              <!-- <year-select @child-changed="onChange" /> -->
              <GenderSelect @child-changed="onChange" />
              <AgeSelect @child-changed="onChange" />
            </section>
            <section class="visualization-controls__row">
              <DvaSelect
                :disabled="
                  countySelections.length > 1 || genderSelections.length == 2
                "
                @child-changed="onChange"
              />
            </section>
            <Buttons @reset="reset" @export="exportDatasets" />
            <div id="#errMsg">{{ visualization.errMsg }}</div>
          </form>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
/**
 * NOTE: Changes made this file, the models, or the api functions need to copied
 * over to Hunt Il.
 */
import { createNamespacedHelpers, mapMutations } from "vuex";
import { get, last, uniq, flatten } from "lodash";
import platform from "platform";
// import Multiselect from "vue-multiselect";
// import lineDefaults from "~/components/visualization/lineDefaults";
import ChartColors from "~/components/visualization/chartColors";
import Buttons from "~/components/visualization/Buttons";
import BarChart from "~/components/visualization/BarChart";
import LineChart from "~/components/visualization/LineChart";
import CountySelect from "~/components/visualization/CountySelect";
import GenderSelect from "~/components/visualization/GenderSelect";
import AgeSelect from "~/components/visualization/AgeSelect";
import SeasonSelect from "~/components/visualization/SeasonSelect";
import DvaSelect from "~/components/visualization/DvaSelect";
import CustomLegend from "~/components/visualization/CustomLegend";
// import DataSelect from "~/components/visualization/DataSelect";
import YearSelect from "~/components/visualization/YearSelect";
// import options from "~/components/visualization/options";
// import SampleData from "~/components/visualization/sampleData";
import VisualizationFunctions from "~/components/visualization/visualizationFunctions";
import VisualizationDataSamples from "~/lib/VisualizationDataSamples";
// import VisualizationData from "~/lib/VisualizationData";
import DatasetFactory from "~/lib/DatasetFactory";
// import DataFitting from "./DataFitting";

// https://vuex.vuejs.org/guide/modules.html#binding-helpers-with-namespace
const { mapState, mapActions } = createNamespacedHelpers("visualization");

// function selectorForSelectedViz(selectedViz, sel = {}) {
//   const newSelector = Object.assign({}, sel);
//   switch (selectedViz) {
//     case "buckHarvest":
//       newSelector.gender = "M";
//       break;

//     case "adultBuckHarvest":
//       newSelector.gender = "M";
//       newSelector.ageClass = "A";
//       break;

//     case "yearlingBuckHarvest":
//       newSelector.gender = "M";
//       newSelector.ageClass = "Y";
//       break;

//     case "buttonBuckHarvest":
//       newSelector.gender = "M";
//       newSelector.ageClass = "F";
//       break;

//     case "doeHarvest":
//       newSelector.gender = "F";
//       break;

//     case "adultDoeHarvest":
//       newSelector.gender = "F";
//       newSelector.ageClass = "A";
//       break;

//     case "doeFawnHarvest":
//       newSelector.gender = "F";
//       newSelector.ageClass = "F";
//       break;

//     case "sexRatio":
//       newSelector.ageClass = "A";
//       break;

//     case "yearlingBucksRatio":
//     case "matureBucksRatio":
//       newSelector.gender = "M";
//       break;

//     default:
//       break;
//   }
//   return newSelector;
// }

function selectorForFilters(counties, seasons, genders, ages) {
  const selector = {};
  selectorForCounties(counties, selector);
  selectorForSeasons(seasons, selector);
  selectorForGenders(genders, selector);
  selectorForAges(ages, selector);
  return selector;
}
function selectorForCounties(counties, selector) {
  if (counties?.length) {
    // selector.county = { $in: counties.map(c => c.toUpperCase()) };
    selector.county = { $in: counties };
  }
}
function selectorForSeasons(seasons, selector) {
  if (seasons?.length) {
    selector.season = { $in: seasons.map((s) => s.value) };
  }
}
function selectorForGenders(genders, selector) {
  if (genders.length) {
    selector.gender = { $in: genders.map((g) => (g === "Male" ? "M" : "F")) };
  }
}
function selectorForAges(ages, selector) {
  if (ages.length) {
    selector.ageClass = { $in: ages.map((o) => o.value) };
  }
}

function createChartDatasets(harvestData, query) {
  const datasets = [];
  // const counties = query?.sel?.county?.$in;
  const counties = query?.counties;
  const genders = query?.sel?.gender?.$in;
  const ages = query?.sel?.ageClass?.$in;
  const seasons = query?.sel?.season?.$in;
  const groups = [];

  if (counties?.length) {
    groups.push({ fieldName: "county", values: counties });
  }
  if (genders?.length) {
    groups.push({ fieldName: "gender", values: genders });
  }
  if (ages?.length) {
    groups.push({ fieldName: "ageClass", values: ages });
  }
  if (seasons?.length) {
    groups.push({ fieldName: "season", values: seasons });
  }
  if (groups.length > 1) {
    datasets.push(...DatasetFactory.create(harvestData, groups, query));
  } else {
    const fieldName = groups[0]?.fieldName;
    if (fieldName) {
      datasets.push(...DatasetFactory.create(harvestData, fieldName, query));
    } else {
      datasets.push(...DatasetFactory.create(harvestData));
    }
  }

  // NOTE: This is the default total deer harvest
  if (datasets.length === 0) {
    datasets.push(...DatasetFactory.create(harvestData));
  }
  // if (data.length === 0) {
  //   data.push(
  //     years.map(yr => {
  //       let val = harvestData
  //         .filter(h => h.year === yr)
  //         .reduce((acc, currentVal) => {
  //           acc = acc + currentVal.value;
  //           return acc;
  //         }, 0);
  //       if (val === 0) {
  //         val = null;
  //       }
  //       return {
  //         year: yr,
  //         selector: "-",
  //         value: val
  //       };
  //     })
  //   );
  // }

  // const xAxis = options.scales.xAxes[0];
  // // const leftYAxis = options.scales.yAxes.find(obj => obj.id === "y-axis-0");
  // const rightYAxis = options.scales.yAxes.find(obj => obj.id === "y-axis-1");
  // xAxis.offset = false;
  // rightYAxis.display = false;
  // data.forEach(d => {
  //   const dataPoints = uniq(d.map(obj => obj.value));
  //   if (dataPoints.length !== 1 && dataPoints[0] !== null) {
  //     const selector = d[0].selector.slice(d[0].selector.indexOf("-") + 1);
  //     const harvestDataset = Object.assign(
  //       {},
  //       lineDefaults,
  //       {
  //         label: `${selector} Deer Harvested`,
  //         yAxisID: "y-axis-0",
  //         backgroundColor: ChartColors.black,
  //         borderColor: ChartColors.totalBorder,
  //         pointHoverBorderColor: ChartColors.black,
  //         data: d.map(obj => obj.value)
  //       },
  //       lineColors(selector)
  //     );
  //     datasets.push(harvestDataset);
  //   }
  // });

  return datasets;
}

export default {
  name: "Visualizations",

  components: {
    BarChart,
    Buttons,
    CountySelect,
    CustomLegend,
    // DataSelect,
    GenderSelect,
    AgeSelect,
    SeasonSelect,
    DvaSelect,
    LineChart,
    YearSelect,
  },

  data() {
    return {
      default: true,
      options: VisualizationFunctions.getChartOptions("default"),
      query: {},
      apiCalls: [],
      hiddenDatasetIndex: null,
    };
  },

  computed: {
    ...mapState({
      chartSelection: (state) => state.chartSelection, // ???
      customLegend: (state) => state.legend,
      showChart: (state) => state.viewAs === "chart",
      chartData: (state) => state.chartData,
      chartOptions: (state) => state.chartOptions,
      beginYear: (state) => state.beginYear,
      endYear: (state) => state.endYear,
      countySelections: (state) => state.countySelections,
      seasonSelections: (state) => state.seasonSelections,
      genderSelections: (state) => state.genderSelections,
      ageSelections: (state) => state.ageSelections,
      dvaSelection: (state) => state.dvaSelection,
    }),
    // Not sure if this will work to make everything reactive, but hoping so!
    visualization() {
      return this.$store.state.visualization;
    },
    isSafari() {
      return platform.name.match(/Safari/) && Number(platform.version) < 14;
    },
    chartSelection() {
      return this.$store.state.visualization.chartSelection;
    },
    isLineChart() {
      // return VisualizationFunctions.isLineChart(
      //   this.$store.state.visualization.chartSelection.value
      // );
      const dvaSelection = this.dvaSelection;
      const genderSelections = this.genderSelections;
      const countySelections = this.countySelections;
      const ageSelections = this.ageSelections;
      const seasonSelections = this.seasonSelections;
      let result = true;
      // NOTE: Any chart with dva is a Bar chart
      if (dvaSelection === "Yes") {
        result = false;
      }
      if (this.default || genderSelections?.length === 2) {
        if (countySelections?.length > 1) {
        } else {
          if (ageSelections?.length > 1 || seasonSelections?.length > 1) {
            result = true;
          } else {
            result = false;
          }
        }
      }

      if (
        this.chartData?.datasets?.length === 1 &&
        this.chartData?.datasets[0]?.type === "bar"
      ) {
        result = false;
      }
      return result;
    },
  },

  // watch: {
  //   "chartSelection.value": function(type) {
  //     this.options = VisualizationFunctions.getChartOptions(type);
  //   }
  // },

  async created() {
    try {
      const {
        data: { years },
      } = await this.$axios.get("/api/sample_totals/years");
      const begin = Math.min(...years);
      const end = Math.max(...years);
      this.setBeginYear(begin);
      this.setEndYear(end);
      this.setAllYearLabels(VisualizationFunctions.rangeInclude(begin, end));

      const params = {
        // selectedViz: this.chartSelection.value,
        beginYear: this.beginYear,
        endYear: this.endYear,
      };
      // const data = await this.getTotalsFor(params);
      const {
        data: { samplesTotals: samplesTotals },
      } = await this.$axios.get("/api/sample_totals", { params });
      const harvests = samplesTotals.harvests;
      const dvarates = samplesTotals.dvarates;
      const data = { harvests, dvarates };
      const results = this.createChartDatasets(years, data);
      const newChartData = {
        datasets: results.datasets,
        labels: results.years,
      };
      this.setChartData({
        data: newChartData,
        selector: selectorForFilters(
          this.countySelections,
          this.seasonSelections,
          this.genderSelections,
          this.ageSelections
        ),
      });
      // await this.resetVisualization();
    } catch (e) {
      console.error(e);
    }
  },

  methods: {
    ...mapMutations({
      setBeginYear: "visualization/setBeginYear",
      setEndYear: "visualization/setEndYear",
      setAllYearLabels: "visualization/setAllYearLabels",
      setChartData: "visualization/setChartData",
      setChartOptions: "visualization/setChartOptions",
    }),
    // ...mapActions(["resetVisualization"]),

    reset() {
      this.default = true;
    },

    exportDatasets() {
      const datasets = this.$store.state?.visualization?.chartData?.datasets;
      const labels = this.$store.state?.visualization?.chartData?.labels;
      // console.log("labels:", labels);
      // datasets.forEach(ds => console.log("ds:", ds.label, ds.data));
      const rows = [];
      rows.push(["Dataset", ...labels]);
      datasets.forEach((ds) =>
        rows.push([`"${ds.label}"`, ...ds.data.map((d) => `"${d}"`)])
      );
      rows.forEach((row, idx) => {
        row.forEach((cell, cellIdx) => {
          if (cell === '"null"') {
            row[cellIdx] = '""';
          }
        });
      });
      // console.log(rows);
      const csvContent = `data:text/csv:charset=utf-8,${rows
        .map((r) => r.join(","))
        .join("\n")}`;
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", "export.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
    },

    displayYear(type = "begin") {
      const visualization = this.$store.state.visualization;
      const year = get(visualization, `${type}Year`, null);
      if (year) {
        return year;
      }
      return null;
    },

    hideOtherDatasets(index) {
      const datasets = [...this.chartData.datasets];
      datasets.forEach((ds, i) => {
        if (i !== index && this.hiddenDatasetIndex !== index) {
          ds.hidden = true;
        } else {
          ds.hidden = false;
        }
      });
      if (this.hiddenDatasetIndex === index) {
        this.hiddenDatasetIndex = null;
      } else {
        this.hiddenDatasetIndex = index;
      }
      const newChartData = {
        datasets: datasets,
        labels: this.chartData.labels,
      };
      this.setChartData({
        data: newChartData,
        selector: selectorForFilters(
          this.countySelections,
          this.seasonSelections,
          this.genderSelections,
          this.ageSelections
        ),
      });
    },
    /**
     * [createChartDatasets description]
     *
     * @param   {number[]}  years
     * @param   {object[]}  samples
     * @param   {string[]}  counties
     *
     * @return  {object[]}
     */
    createChartDatasets(years, samples, counties) {
      let dva = samples?.dvarates || samples?.totalsFor?.dva;
      let harvest = samples?.harvests || samples?.totalsFor?.harvest; // [{ year: 2005, value: x },...]
      if (!years) {
        years = harvest.map((h) => h.year);
      }
      // let harvestDatasetOptions;
      const goals = samples.goals;
      const datasets = [];
      let statewide = false;
      if (!counties?.length) {
        statewide = true;
      }

      // if (this.chartSelection.value !== "default") {
      //   let harvestYears = harvest
      //     .filter(obj => {
      //       if (obj.value) {
      //         return obj.year;
      //       }
      //     })
      //     .map(obj => obj.year);
      //   // let harvestYears = uniq(harvest.map(h => h.year));
      //   const harvestBeginYear = Math.min(...harvestYears);
      //   const harvestEndYear = Math.max(...harvestYears);
      //   this.setBeginYear(harvestBeginYear);
      //   this.setEndYear(harvestEndYear);
      //   harvestYears = VisualizationFunctions.rangeInclude(
      //     harvestBeginYear,
      //     harvestEndYear
      //   );
      //   // this.setAllYearLabels(harvestYears);
      //   years = harvestYears;
      //   harvestDatasetOptions = VisualizationFunctions.getDatasetOptions(
      //     this.chartSelection.value
      //   );
      // }

      // eslint-disable-next-line no-shadow
      // const options = this.options;
      // const xAxis = options.scales.xAxes[0];
      // const leftYAxis = options.scales.yAxes.find(obj => obj.id === "y-axis-0");
      // const rightYAxis = options.scales.yAxes.find(
      //   obj => obj.id === "y-axis-1"
      // );
      // let graphType = "line";
      // if (counties.length > 1) {
      //   if (dva) {
      //     dva = VisualizationDataSamples.yValuesFor(years, dva);
      //   }
      //   harvest = VisualizationDataSamples.yValuesFor(years, harvest);
      // } else {
      //   if (dva) {
      //     dva = dva.map(o => o.value || null);
      //   }
      //   let val;
      //   // harvest = years.map(yr => {
      //   //   val = harvest
      //   //     .filter(h => h.year === yr)
      //   //     .reduce((acc, currentVal) => {
      //   //       acc = acc + currentVal.value;
      //   //       return acc;
      //   //     }, 0);
      //   //   if (val === 0) {
      //   //     val = null;
      //   //   }
      //   //   return { year: yr, value: val };
      //   // });
      //   // harvest = harvest.map(obj => obj.value);
      // }
      if (statewide) {
        // const harvestDataset = createChartDatasets(harvest)[0];
        const harvestDataset = createChartDatasets(harvest, this.query);
        harvestDataset.label = "Total Deer Harvested";
        datasets.push(...harvestDataset);
      } else {
        const chartDatasets = createChartDatasets(
          harvest,
          this.query,
          this.options
        );
        datasets.push(...chartDatasets);
      }

      // xAxis.offset = false;
      // rightYAxis.display = true;
      // let harvestDataset = Object.assign({}, lineDefaults, {
      //   label: "Deer Harvested",
      //   yAxisID: "y-axis-0",
      //   backgroundColor: ChartColors.black,
      //   borderColor: ChartColors.totalBorder,
      //   pointHoverBorderColor: ChartColors.black,
      //   data: harvest
      // });
      // if (harvestDatasetOptions) {
      //   harvestDataset = Object.assign(harvestDataset, harvestDatasetOptions);
      // }
      // datasets.push(harvestDataset);

      // if (dva) {
      //   datasets.push(
      //     Object.assign({}, lineDefaults, {
      //       label: "DVA Rate",
      //       yAxisID: "y-axis-1",
      //       backgroundColor: ChartColors.barlightblue,
      //       borderColor: ChartColors.blue,
      //       pointHoverBorderColor: ChartColors.blue,
      //       data: dva
      //     })
      //   );
      // }

      // // Goals lines
      // if (goals) {
      //   const oldGoals = years.map(y => samples.goals.origGoal);
      //   const newGoals = years.map(y => samples.goals.newGoal);
      //   datasets.push(
      //     Object.assign({}, lineDefaults, {
      //       label: "Old Goal",
      //       yAxisID: "y-axis-1",
      //       pointRadius: 0,
      //       backgroundColor: ChartColors.gray,
      //       borderDash: [5, 2],
      //       borderColor: ChartColors.gray,
      //       pointHoverBorderColor: ChartColors.gray,
      //       data: oldGoals
      //     })
      //   );
      //   datasets.push(
      //     Object.assign({}, lineDefaults, {
      //       label: "Goal",
      //       yAxisID: "y-axis-1",
      //       pointRadius: 0,
      //       backgroundColor: ChartColors.lightorange,
      //       borderColor: ChartColors.orange,
      //       pointHoverBorderColor: ChartColors.orange,
      //       data: newGoals
      //     })
      //   );
      // }
      if (goals) {
        goals.forEach((g) => {
          const goalsData = VisualizationDataSamples.yValuesForGoals(
            years,
            g.origGoal,
            g.newGoal
          );
          const goalsDataset = {
            type: "line",
            label: "DVA Goal (first used in 2008, revised in 2014)",
            yAxisID: "y-axis-1",
            backgroundColor: ChartColors.orange,
            borderColor: ChartColors.orange,
            pointHoverBorderColor: ChartColors.orange,
            fill: false,
            lineTension: 0,
            pointRadius: 0,
            pointHoverRadius: 0,
            pointHitRadius: 4,
            borderWidth: 4,
            data: goalsData,
          };
          datasets.push(goalsDataset);
        });
      }
      if (dva) {
        // const dvaDataset = createChartDatasets(dva)[0];
        // dvaDataset.type = "bar";
        // dvaDataset.label = "Deer Vehicle Accident Rate";
        // dvaDataset.yAxisID = "y-axis-1";
        // dvaDataset.categoryPercentage = 1;
        // dvaDataset.barPercentage = 0.75;
        // dvaDataset.backgroundColor = ChartColors.barlightblue;
        // dvaDataset.borderColor = ChartColors.blue;
        // dvaDataset.pointHoverBorderColor = ChartColors.blue;
        // dvaData.push(null);
        // dvaData.unshift(null);
        const dvaData = VisualizationDataSamples.yValuesFor(years, dva);
        const dvaDataset = {
          type: "bar",
          label: "Deer-Vehicle Accident (DVA)",
          yAxisID: "y-axis-1",
          categoryPercentage: 1,
          barPercentage: 0.75,
          backgroundColor: ChartColors.lightblue,
          // borderColor: ChartColors.lightblue,
          pointHoverBorderColor: ChartColors.blue,
          data: dvaData,
        };
        datasets.push(dvaDataset);
        // const xAxis = this.options.scales.xAxes[0];
        // xAxis.offset = false;
        // const leftYAxis = this.options.scales.yAxes.find(obj => obj.id === "y-axis-0");
      }
      if (dva || goals) {
        const rightYAxis = this.options.scales.yAxes.find(
          (obj) => obj.id === "y-axis-1"
        );
        rightYAxis.display = true;
      } else {
        const rightYAxis = this.options.scales.yAxes.find(
          (obj) => obj.id === "y-axis-1"
        );
        rightYAxis.display = false;
      }
      return { datasets: datasets, years: years };
    },

    async getSamplesFor(params) {
      console.log("getSamplesFor");
      const url = "/api/samples_for";
      this.apiCalls.push(this.$axios.get(url, { params }));
      const results = await Promise.all(this.apiCalls);
      const result = last(results);
      this.apiCalls = [];
      const years = VisualizationFunctions.rangeInclude(
        this.beginYear,
        this.endYear
      );
      const {
        data: { samplesFor: samplesFor, goals: goals },
      } = result;
      const data = {};
      let field = "";
      const obj = samplesFor[0];
      if (obj.dvarate) {
        const dva = samplesFor.map((d) => ({
          year: d.year,
          value: d.dvarate,
        }));
        data.dva = dva;
      }
      let harvest;
      if (obj.harvest) {
        harvest = samplesFor.map((d) => ({
          year: d.year,
          value: d.harvest,
        }));
        data.harvest = harvest;
      } else if (obj.hasOwnProperty("total")) {
        harvest = samplesFor.map((d) => ({
          year: d.year,
          value: d.total,
        }));
        data.harvest = harvest;
      }
      if (goals) {
        data.goals = goals;
      }
      return data;
    },

    async getHarvestData(params) {
      const { data } = await this.$axios.get("/api/harvest_query", { params });
      console.log("getHarvestData:", data);
      return data;
    },

    async getTotalsFor(params) {
      console.log("getTotalsFor");
      const url = "/api/sample_totals";
      this.apiCalls.push(this.$axios.get(url, { params }));
      const results = await Promise.all(this.apiCalls);
      const result = last(results);
      this.apiCalls = [];
      const years = VisualizationFunctions.rangeInclude(
        this.beginYear,
        this.endYear
      );
      const {
        data: { samplesTotals: samplesTotals, goals: goals },
      } = result;
      const totalsFor = {};
      // if (params.selectedViz === "default") {
      //   totalsFor.dva = samplesTotals.dvarates;
      // }
      let harvest = samplesTotals.harvests;

      // if (params.selectedViz === "trend") {
      //   const dataFit = new DataFitting(years, harvest).trendVals();
      //   harvest = years.map((y, idx) => ({
      //     year: y,
      //     value: dataFit[idx]
      //   }));
      // }
      totalsFor.harvest = harvest;

      return { totalsFor, goals };
    },

    async onChange(_arg) {
      this.default = false;
      const selector = selectorForFilters(
        this.countySelections,
        this.seasonSelections,
        this.genderSelections,
        this.ageSelections
      );
      if (selector?.county?.$in) {
        selector.county.$in = flatten(
          // selector.county.$in.map(c => [c, c.replace(/\s|\./g, "")])
          selector.county.$in.map((c) => c.replace(/\s|\./g, ""))
        );
      }
      const params = {
        beginYear: this.beginYear,
        endYear: this.endYear,
        sel: selector,
      };
      console.log({ params });
      const counties = this.countySelections;
      if (counties) {
        params.counties = counties;
      }
      this.query = params;
      let years;
      let data;
      // const datasets = [];
      const newChartData = { datasets: null, labels: null };
      const options = this.options;
      const leftYAxis = options.scales.yAxes.find(
        (obj) => obj.id === "y-axis-0"
      );
      const rightYAxis = options.scales.yAxes.find(
        (obj) => obj.id === "y-axis-1"
      );
      delete rightYAxis.ticks.suggestedMax;
      try {
        data = await this.getHarvestData(params);
        if (data) {
          years = uniq(data.map((obj) => obj.year)).sort();
          data = { harvests: data };
          if (params?.counties?.length === 1 && this.dvaSelection === "Yes") {
            const { data: dva } = await this.$axios.get("/api/dvarates", {
              params: { county: params.counties[0] },
            });
            data.dvarates = dva[0]?.data;
          }
          if (params?.counties?.length === 1 && this.dvaSelection === "Yes") {
            const { data: goals } = await this.$axios.get("/api/goals", {
              params: { county: params.counties[0] },
            });
            if (goals) {
              data.goals = goals;
              rightYAxis.ticks.suggestedMax =
                goals[0].newGoal +
                Math.ceil(Math.ceil(goals[0].newGoal) * 0.05);
            }
          }
          const result = this.createChartDatasets(
            years,
            data,
            this.countySelections
          );
          options.tooltips.caretSize = 0;
          // options.tooltips.mode = "nearest";
          const xAxis = options.scales.xAxes[0];
          if (
            this.countySelections?.length < 2 &&
            this.genderSelections?.length === 2 &&
            this.ageSelections?.length < 2 &&
            this.seasonSelections?.length < 2
          ) {
            xAxis.stacked = true;
            leftYAxis.stacked = true;
            result.datasets.forEach((ds) => {
              if (ds.label.match(/gender/) || ds.label === "Deer Harvested") {
                ds.type = "bar";
                if (ds.label.match(/gender-M/)) {
                  ds.backgroundColor = ChartColors.male;
                  ds.borderColor = ChartColors.male;
                } else {
                  ds.backgroundColor = ChartColors.female;
                  ds.borderColor = ChartColors.female;
                }
              }
            });
          } else {
            xAxis.stacked = false;
            leftYAxis.stacked = false;
          }
          newChartData.datasets = result.datasets;
          newChartData.labels = result.years;
        }
      } catch (e) {
        console.error(e);
      }

      // if (["default", "trend"].includes(this.chartSelection.value)) {
      //   try {
      //     data = await this.getTotalsFor(params);
      //     if (data) {
      //       const result = this.createChartDatasets(
      //         this.visualization.chartSelection.value,
      //         years,
      //         data,
      //         this.countySelections
      //       );
      //       newChartData.datasets = result.datasets;
      //       newChartData.labels = result.years;
      //     }
      //   } catch (e) {
      //     console.error(e);
      //     // this.rollbar.error(e);
      //   }
      //   // } else if (this.chartSelection.value === "sexRatio") {
      // } else if (
      //   ["sexRatio", "yearlingBucksRatio", "matureBucksRatio"].includes(
      //     this.chartSelection.value
      //   )
      // ) {
      //   try {
      //     data = await this.getHarvestData(params);
      //     years = uniq(data.map(obj => obj.year)).sort();
      //   } catch (e) {
      //     console.error(e);
      //     // this.rollbar.error(e);
      //   }
      //   if (this.chartSelection.value === "sexRatio") {
      //     const male = data.filter(s => s.gender === "M");
      //     const female = data.filter(s => s.gender === "F");
      //     datasets.push(
      //       {
      //         label: "Male",
      //         yAxisId: "y-axis-0",
      //         backgroundColor: ChartColors.buckBorder,
      //         borderColor: ChartColors.buckBorder,
      //         borderWidth: 2,
      //         pointHoverBorderColor: ChartColors.buckBorder,
      //         data: VisualizationDataSamples.yValuesFor(years, male)
      //       },
      //       {
      //         label: "Female",
      //         yAxisId: "y-axis-0",
      //         backgroundColor: ChartColors.doeBorder,
      //         borderColor: ChartColors.doeBorder,
      //         borderWidth: 2,
      //         pointHoverBorderColor: ChartColors.doeBorder,
      //         data: VisualizationDataSamples.yValuesFor(years, female)
      //       }
      //     );
      //   } else if (this.chartSelection.value === "yearlingBucksRatio") {
      //     const yearling = data.filter(s => s.ageClass === "Y");
      //     datasets.push(
      //       {
      //         label: "Yearling",
      //         yAxisId: "y-axis-0",
      //         backgroundColor: ChartColors.yearlingBuckBorder,
      //         borderColor: ChartColors.yearlingBuckBorder,
      //         borderWidth: 2,
      //         pointHoverBorderColor: ChartColors.yearlingBuckBorder,
      //         data: VisualizationDataSamples.yValuesFor(years, yearling)
      //       },
      //       {
      //         label: "Total",
      //         yAxisId: "y-axis-0",
      //         backgroundColor: ChartColors.buckBorder,
      //         borderColor: ChartColors.buckBorder,
      //         borderWidth: 2,
      //         pointHoverBorderColor: ChartColors.buckBorder,
      //         data: VisualizationDataSamples.yValuesFor(years, data)
      //       }
      //     );
      //   } else if (this.chartSelection.value === "matureBucksRatio") {
      //     const mature = data.filter(s => s.ageClass === "A");
      //     datasets.push(
      //       {
      //         label: "Mature",
      //         yAxisId: "y-axis-0",
      //         backgroundColor: ChartColors.adultBuckBorder,
      //         borderColor: ChartColors.adultBuckBorder,
      //         borderWidth: 2,
      //         pointHoverBorderColor: ChartColors.adultBuckBorder,
      //         data: VisualizationDataSamples.yValuesFor(years, mature)
      //       },
      //       {
      //         label: "Total",
      //         yAxisId: "y-axis-0",
      //         backgroundColor: ChartColors.buckBorder,
      //         borderColor: ChartColors.buckBorder,
      //         borderWidth: 2,
      //         pointHoverBorderColor: ChartColors.buckBorder,
      //         data: VisualizationDataSamples.yValuesFor(years, data)
      //       }
      //     );
      //   }
      //   newChartData.datasets = datasets;
      //   newChartData.labels = years;
      // } else {
      //   try {
      //     data = await this.getHarvestData(params);
      //     if (data) {
      //       years = uniq(data.map(obj => obj.year)).sort();
      //       data = { harvest: data };
      //       const result = this.createChartDatasets(
      //         this.visualization.chartSelection.value,
      //         years,
      //         data,
      //         this.countySelections
      //       );
      //       newChartData.datasets = result.datasets;
      //       newChartData.labels = result.years;
      //     }
      //   } catch (e) {
      //     console.error(e);
      //     // this.rollbar.error(e);
      //   }
      // }
      // if (years) {
      //   this.setBeginYear(Math.min(...years));
      //   this.setEndYear(Math.max(...years));
      // }
      // const newChartOptions = VisualizationFunctions.getChartOptions(
      //   this.visualization.chartSelection.value
      // );
      this.setChartData({
        data: newChartData,
        selector: selectorForFilters(
          this.countySelections,
          this.seasonSelections,
          this.genderSelections,
          this.ageSelections
        ),
      });
      // this.setChartOptions(newChartOptions);
    },
  },
};
</script>

<style lang="scss">
.visualization {
  .multiselect__option--highlight {
    background: #578384;
  }
  .multiselect__tag {
    background: #578384;
  }
  .multiselect__tag-icon {
    &:after {
      color: #dee0e0;
    }
    &:hover:after {
      color: white;
    }
    &:hover {
      background: #4e7777;
    }
  }
  width: 100%;
  @include tablet() {
    width: 80%;
  }
  margin: auto;
  margin-top: 1rem;
  &__computed {
    padding-left: 0;
    color: black;
  }
  &__inner {
    background-color: white;
    height: 100%;
    padding: 0 1rem;
    &--no-safari {
      position: relative;
    }
  }

  &__loader {
    //width: 80%;
    text-align: center;
    margin: auto;
    margin-bottom: 2rem;
    clear: both;
  }
  %visualization_global {
    min-height: 440px;
    margin: auto;
    margin-bottom: 1rem;
    clear: both;
  }
  &__canvas {
    @extend %visualization_global;
    //width: 80%;
    canvas {
      padding-top: 1em;
    }
    &--hide {
      display: none;
    }
  }
  &__disclaimer {
    font-size: 75%;
    font-style: italic;
    text-align: center;
    width: 100%;
  }
  &__control-button {
    @include visualization-button();
    margin-left: 0;
    @include tablet() {
      margin-left: 1em;
    }
    &:first-child {
      @include mobile() {
        margin-left: 0;
        margin-right: 0.25rem;
      }
    }
    margin-right: 0.25rem;
  }
  &__no-safari {
    left: 0;
    margin-top: 2rem;
    padding: 0 0.5rem;
    position: absolute;
    text-align: center;
    top: 0;
    width: 100%;
  }
  &-controls {
    &__row {
      display: flex;
      flex-wrap: wrap;
      margin-left: -1rem;
      & + & {
        margin-top: 0.5rem;
      }
    }
    &__select {
      width: 100%;
      max-width: 100%;
      margin-top: 0.5rem;
      @include tablet() {
        width: 270px;
        margin-left: 1rem;
        // & + & {
        //   margin-left: 1rem;
        // }
      }
    }
  }
}
</style>
